import type { TranslationTag } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { DismissIcon, StarsButton, useTrackingLabel } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isNumber } from 'lodash';
import type { ChangeEvent, FormEvent, KeyboardEventHandler, MouseEvent } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import ClearIcon from '@swf/core/assets/icons/clear.svg';
import SearchIcon from '@swf/core/assets/icons/icon-search.svg';
import { select } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';

import {
  TXTCLI_Search,
  TXTWCL_Type_game_nameX_category_or_pr_ELL,
  TXTWCL_Type_game_name_or_providerXX
} from './Reference.messages.json';
import styles from './Reference.scss';

interface Props {
  query: string;
  setQuery(value: string): void;
  className?: string;
  selected?: number;
  setSelected: React.Dispatch<React.SetStateAction<number | undefined>>;
  searchToggle(): void;
  referenceElement: HTMLElement | HTMLFormElement | null;
  setReferenceElement(element: HTMLDivElement | HTMLFormElement | HTMLInputElement | null): void;
  results?: CasinoGame[];
  showOverlay: boolean;
  showGamesList: boolean;
  setShowGamesList(flag: boolean): void;
  selectGame(name: string): void;
}

const Reference: React.FC<Props> = ({
  query,
  setQuery,
  className,
  searchToggle,
  selected,
  setSelected,
  showOverlay,
  referenceElement,
  setReferenceElement,
  results,
  showGamesList,
  setShowGamesList,
  selectGame
}) => {
  const { searchFocused, searchFilled, searchClicked } = useTrackers();
  const trackingName = useTrackingLabel(TXTCLI_Search);
  const isMobile = useSelector(select.device.isMobile);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const { formatMessage } = useIntl();
  const [inputElement, setInputElement] = useState<HTMLInputElement | null>(null);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      setShowGamesList(false);
    },
    [setQuery, setShowGamesList]
  );

  const focusInput = useCallback(() => {
    const element = inputElement ? inputElement : referenceElement;
    element?.focus();
  }, [inputElement, referenceElement]);

  const handleSubmit = (e: FormEvent<HTMLFormElement> | MouseEvent<Element>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!results || !results?.length) {
      return;
    }
    if (isNumber(selected)) {
      selectGame(results[selected].name);
    } else if (trackingName) {
      searchClicked({ buttonName: trackingName, search: query });
    }
    setShowGamesList(true);
    focusInput();
  };

  const handleFocus = () => trackingName && searchFocused(trackingName);

  const trackFill = useCallback(
    () =>
      trackingName && searchFilled({ fieldName: trackingName, fieldType: 'text', strInput: query }),
    [query, searchFilled, trackingName]
  );

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    ({ key }) => {
      const max = results?.length ?? 1;
      switch (key) {
        case 'ArrowDown':
          setSelected((prevValue) => (prevValue !== undefined ? (prevValue + 1) % max : 0));
          break;
        case 'ArrowUp':
          setSelected((prevValue) => (prevValue ? (prevValue - 1) % max : max - 1));
          break;
        case 'Escape':
          setQuery('');
          inputElement?.blur();
          break;
        case 'Enter':
          trackFill();
          break;
      }
    },
    [inputElement, trackFill, results?.length, setQuery, setSelected]
  );

  const referenceClassNames = classNames(className, styles.reference, {
    [styles.mobile]: isMobile,
    [styles.desktopEmbedded]: isDesktopEmbedded,
    [styles.overlaid]: showOverlay
  });

  const placeholder = useMemo(() => {
    let message: TranslationTag = TXTCLI_Search;
    if (isMobile) {
      message = TXTWCL_Type_game_name_or_providerXX;
    } else if (isDesktopEmbedded) {
      message = TXTWCL_Type_game_nameX_category_or_pr_ELL;
    }
    return formatMessage(message);
  }, [formatMessage, isDesktopEmbedded, isMobile]);

  useEffect(() => {
    if (isDesktopEmbedded || isMobile || showGamesList) {
      focusInput();
    }
  }, [focusInput, isDesktopEmbedded, isMobile, showGamesList]);

  const closeButton = useMemo(
    () => (
      <StarsButton
        variant="text"
        className={styles.cancel}
        onClick={searchToggle}
        icon={<DismissIcon data-testid="dismiss-icon" />}
      />
    ),
    [searchToggle]
  );

  return (
    <div
      className={referenceClassNames}
      ref={!isDesktopEmbedded ? setReferenceElement : undefined} // EXCEPT in desktop embedded mode, we want the popper to be aligned with the outter div element
    >
      <form
        className={styles.search}
        action=""
        ref={isDesktopEmbedded && showGamesList ? setReferenceElement : undefined}
        onSubmit={handleSubmit}
      >
        <StarsButton
          data-testid="search-remove-button"
          size="tiny"
          variant="text"
          className={classNames(styles.clear, { [styles.hidden]: !query })}
          onClick={() => setQuery('')}
          icon={<ClearIcon />}
        />
        <input
          data-testid="search-input"
          type="search"
          onFocus={handleFocus}
          onBlur={trackFill}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={styles.input}
          autoCorrect="false"
          value={query}
          autoComplete="false"
          aria-label="search"
          ref={isDesktopEmbedded && !showGamesList ? setReferenceElement : setInputElement}
        />
        {isDesktopEmbedded ? (
          <StarsButton onClick={handleSubmit} translation={TXTCLI_Search} />
        ) : (
          !isMobile && (
            <SearchIcon
              data-testid="search-button"
              className={styles.icon}
              onClick={handleSubmit}
            />
          )
        )}
        {isDesktopEmbedded && closeButton}
      </form>
      {isMobile && closeButton}
    </div>
  );
};

export default Reference;
